import { uuidv7 } from '@pos/core';
import { supabase } from './supabase.ts';

const BUCKET = 'product-images';
const MAX_SIZE = 600;

/**
 * Achica la foto en el navegador antes de subirla (las fotos de celular pesan varios MB):
 * lado mayor 600 px, formato WebP. Queda en ~30–80 KB.
 */
export async function resizeImage(file: File, maxSize = MAX_SIZE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
  if (!blob) throw new Error('No se pudo procesar la imagen.');
  return blob;
}

/** Sube la foto a la carpeta del negocio y devuelve la ruta para guardar en el producto. */
export async function uploadProductImage(businessId: string, productId: string, file: File): Promise<string> {
  if (!navigator.onLine) throw new Error('Para subir una foto hace falta conexión a internet.');
  const blob = await resizeImage(file);
  const path = `${businessId}/${productId}/${uuidv7()}.webp`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/webp',
    cacheControl: '31536000',
    upsert: false,
  });
  if (error) throw new Error(`No se pudo subir la foto: ${error.message}`);
  return path;
}

/**
 * Logo del negocio: va al mismo bucket, en la carpeta del negocio (solo el dueño puede subir ahí).
 * Lado mayor 400 px: alcanza para el ticket (una ticketera de 80 mm imprime ~576 puntos de ancho).
 */
export async function uploadBusinessLogo(businessId: string, file: File): Promise<string> {
  if (!navigator.onLine) throw new Error('Para subir el logo hace falta conexión a internet.');
  const blob = await resizeImage(file, 400);
  const path = `${businessId}/logo/${uuidv7()}.webp`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/webp',
    cacheControl: '31536000',
    upsert: false,
  });
  if (error) throw new Error(`No se pudo subir el logo: ${error.message}`);
  return path;
}

/** Borra un logo que ya no se usa (si falla, solo queda el archivo ocupando unos KB). */
export async function removeBusinessLogoFile(path: string | null | undefined): Promise<void> {
  if (!path || !navigator.onLine) return;
  await supabase.storage.from(BUCKET).remove([path]).catch(() => undefined);
}

/** URL pública del logo (se cachea en el dispositivo como las fotos de productos). */
export function businessLogoUrl(path: string | null | undefined): string | null {
  return productImageUrl(path);
}

/** URL pública de la foto (se cachea en el dispositivo para verla sin conexión). */
export function productImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}
